import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bell, CheckCheck, Inbox, Bot, Truck, AlertTriangle, X, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { reviewAPI } from '../services/api';

export interface NotificationItem {
  id: string;
  type: 'email' | 'ai' | 'shipping' | 'alert';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  ticketId: string;
}

// Format ISO date string into clean relative time (e.g. "2m ago", "1h ago")
function formatRelativeTime(dateStr: string): string {
  if (!dateStr) return 'Just now';
  try {
    const now = new Date();
    const date = new Date(dateStr);
    const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffSec < 60) return 'Just now';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    return `${Math.floor(diffSec / 86400)}d ago`;
  } catch {
    return 'Just now';
  }
}

export const NotificationCenter: React.FC = () => {
  const [readTicketIds, setReadTicketIds] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('edesk_read_ticket_ids');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });

  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [desktopEnabled, setDesktopEnabled] = useState<boolean>(() => {
    return 'Notification' in window && Notification.permission === 'granted';
  });

  const popoverRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Save read status to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('edesk_read_ticket_ids', JSON.stringify(Array.from(readTicketIds)));
    } catch (e) {
      console.error('Failed to save notification read status:', e);
    }
  }, [readTicketIds]);

  // Click outside to close popover
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 100% Real-time database poll for pending review emails (polling every 3 seconds)
  const { data: queueData } = useQuery({
    queryKey: ['realtimeNotificationQueue'],
    queryFn: async () => {
      const res = await reviewAPI.getPending({ limit: 15 });
      return res.data;
    },
    refetchInterval: 3000,
  });

  // Construct 100% real notification items directly from database queue
  const realNotifications: NotificationItem[] = React.useMemo(() => {
    if (!queueData || !Array.isArray(queueData.emails)) {
      return [];
    }

    return queueData.emails.map((email: any) => {
      const customerName = email.from_address
        ? email.from_address.split('@')[0].replace(/[._]/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
        : 'Customer';
      
      const category = (email.category || '').toLowerCase();
      let type: 'email' | 'ai' | 'shipping' | 'alert' = 'email';
      
      if (category.includes('shipping') || category.includes('tracking') || category.includes('delivery')) {
        type = 'shipping';
      } else if (category.includes('return') || category.includes('claim') || category.includes('cancel')) {
        type = 'alert';
      } else if (email.response_text) {
        type = 'ai';
      }

      const orderId = email.extracted_entities?.order_id || email.extracted_entities?.extracted_order_id;
      const orderTag = orderId ? ` (Order #${orderId})` : '';

      return {
        id: email.id,
        type,
        title: `Ticket #${email.id.substring(0, 6)}: ${customerName}`,
        message: `${email.subject || 'Customer Inquiry'}${orderTag}`,
        timestamp: formatRelativeTime(email.received_at || email.created_at),
        read: readTicketIds.has(email.id),
        ticketId: email.id,
      };
    });
  }, [queueData, readTicketIds]);

  const unreadCount = realNotifications.filter(n => !n.read).length;

  const handleMarkAllRead = () => {
    const allIds = new Set(realNotifications.map(n => n.ticketId));
    setReadTicketIds(allIds);
    toast.success('All notifications marked as read');
  };

  const handleNotificationClick = (item: NotificationItem) => {
    setReadTicketIds(prev => new Set(prev).add(item.ticketId));
    setIsOpen(false);
    navigate(`/review/${item.ticketId}`);
  };

  const toggleDesktopNotifications = async () => {
    if (!('Notification' in window)) {
      toast.error('Browser does not support desktop notifications');
      return;
    }

    if (Notification.permission === 'granted') {
      toast.success('Desktop notifications active');
      setDesktopEnabled(true);
    } else {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setDesktopEnabled(true);
        toast.success('Desktop push notifications enabled!');
        new Notification('eDesk Notifications Enabled', {
          body: 'You will receive real-time ticket alerts in your browser.',
        });
      } else {
        toast.error('Notification permission denied');
        setDesktopEnabled(false);
      }
    }
  };

  const filteredList = filter === 'unread' ? realNotifications.filter(n => !n.read) : realNotifications;

  return (
    <div className="relative inline-block" ref={popoverRef}>
      {/* Bell Button with Unread Badge */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-full relative transition-colors cursor-pointer"
        title="Notifications"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full border-2 border-white flex items-center justify-center animate-pulse">
            {unreadCount}
          </span>
        )}
      </button>

      {/* Notification Dropdown Popover */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
          {/* Header */}
          <div className="p-3.5 bg-slate-900 text-white flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Bell className="w-4 h-4 text-blue-400" />
              <span className="font-bold text-sm tracking-tight">Notifications</span>
              {unreadCount > 0 && (
                <span className="bg-blue-600 text-white text-[11px] px-2 py-0.5 rounded-full font-semibold">
                  {unreadCount} new
                </span>
              )}
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={handleMarkAllRead}
                className="p-1 hover:bg-slate-800 text-slate-300 hover:text-white rounded transition-colors"
                title="Mark all as read"
              >
                <CheckCheck className="w-4 h-4" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-slate-800 text-slate-400 hover:text-white rounded transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Sub-header Filter Tabs */}
          <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between text-xs">
            <div className="flex items-center space-x-1 bg-slate-200/70 p-0.5 rounded-lg">
              <button
                onClick={() => setFilter('all')}
                className={`px-2.5 py-1 rounded-md font-semibold transition-all ${
                  filter === 'all' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                All ({realNotifications.length})
              </button>
              <button
                onClick={() => setFilter('unread')}
                className={`px-2.5 py-1 rounded-md font-semibold transition-all ${
                  filter === 'unread' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Unread ({unreadCount})
              </button>
            </div>

            <button
              onClick={toggleDesktopNotifications}
              className={`flex items-center space-x-1 text-[11px] font-semibold px-2 py-1 rounded transition-colors ${
                desktopEnabled ? 'text-emerald-700 bg-emerald-50' : 'text-blue-600 hover:bg-blue-50'
              }`}
              title="Toggle browser push alerts"
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>{desktopEnabled ? 'Push Active' : 'Enable Push'}</span>
            </button>
          </div>

          {/* Notifications List */}
          <div className="max-h-[340px] overflow-y-auto divide-y divide-slate-100">
            {filteredList.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-xs flex flex-col items-center">
                <Bell className="w-8 h-8 stroke-1 text-slate-300 mb-2" />
                <p className="font-semibold text-slate-600">No notifications</p>
                <p className="text-[11px] text-slate-400">You're all caught up!</p>
              </div>
            ) : (
              filteredList.map(item => (
                <div
                  key={item.id}
                  onClick={() => handleNotificationClick(item)}
                  className={`p-3 flex items-start space-x-3 cursor-pointer transition-colors ${
                    !item.read ? 'bg-blue-50/50 hover:bg-blue-50' : 'hover:bg-slate-50'
                  }`}
                >
                  {/* Icon */}
                  <div className="mt-0.5 shrink-0">
                    {item.type === 'email' && (
                      <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
                        <Inbox className="w-4 h-4" />
                      </div>
                    )}
                    {item.type === 'ai' && (
                      <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center">
                        <Bot className="w-4 h-4" />
                      </div>
                    )}
                    {item.type === 'shipping' && (
                      <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center">
                        <Truck className="w-4 h-4" />
                      </div>
                    )}
                    {item.type === 'alert' && (
                      <div className="w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center">
                        <AlertTriangle className="w-4 h-4" />
                      </div>
                    )}
                  </div>

                  {/* Body */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className={`text-xs font-bold ${!item.read ? 'text-slate-900' : 'text-slate-700'}`}>
                        {item.title}
                      </p>
                      <span className="text-[10px] text-slate-400 shrink-0 ml-2">{item.timestamp}</span>
                    </div>
                    <p className="text-[11px] text-slate-600 line-clamp-2 mt-0.5 leading-snug">
                      {item.message}
                    </p>
                  </div>

                  {/* Unread Indicator Dot */}
                  {!item.read && (
                    <div className="w-2 h-2 rounded-full bg-blue-600 shrink-0 mt-1.5" />
                  )}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="p-2 bg-slate-50 border-t border-slate-200 text-center">
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/emails');
              }}
              className="text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors cursor-pointer"
            >
              View all tickets in Mailbox →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
